import React, { FC, useCallback, useMemo } from 'react';
import { TournamentPlayerStatus } from '@okletsplay/core';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import classNames from 'classnames';
import config from 'config/config';
import { TournamentAlert } from 'shared/components/TournamentAlert';
import { TournamentLobbyInvitation } from 'modules/tournaments/interfaces';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import { updateInvitationStatus } from 'modules/tournaments/store/current-tournament/repository';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { useStyles } from 'modules/tournaments/components/BracketTournament/TournamentInvitationList/TournamentInvitation/TournamentInvitation.styles';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { usePlatformLinkAction } from 'shared/hooks';
import { WithPaidActionButton } from 'modules/game-payments/components';
import { useJoinInvitationTournamentForm } from 'modules/tournaments/hooks/use-join-invitation-tournament-form';
import { FormProvider } from 'react-hook-form';
import { Box } from '@mui/material';

interface TournamentInvitationProps {
  invitation: TournamentLobbyInvitation;
}

export const TournamentInvitation: FC<TournamentInvitationProps> = ({ invitation }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const { form } = useJoinInvitationTournamentForm();

  const updating = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.UpdatingInvitationStatus,
  });

  const tournament = useSelector(currentTournamentSelector.tournament);
  const entryFee = tournament?.prizeAndDistribution?.playerEntryFee;

  const { openPlatformLink, isPlatformLinked } = usePlatformLinkAction(
    tournament?.game?.platformType
  );

  const checkinTime = useMemo(() => {
    if (!invitation?.startedAt) {
      return null;
    }

    const checkinDate = moment(invitation.startedAt).subtract(
      config.timeToTournamentCheckIn,
      'seconds'
    );

    const duration = moment.duration(checkinDate.diff(moment()));
    const days = duration.days();
    const hours = duration.hours();

    if (duration.asSeconds() <= 0) {
      return null;
    }

    if (days >= 1) {
      return `${duration.days()}d`;
    }

    if (days < 1 && hours >= 1) {
      return `${duration.hours()}h`;
    }

    return `${duration.minutes()}m`;
  }, [invitation?.startedAt]);

  const onClickHandler = useCallback(
    (status: TournamentPlayerStatus) => {
      if (!isPlatformLinked && status === TournamentPlayerStatus.Standby) {
        openPlatformLink();
        return;
      }

      if (!invitation) {
        return;
      }

      dispatch(
        updateInvitationStatus({
          identifier: invitation.tournamentId,
          data: { ...form.getValues(), teamId: invitation.teamId, status },
        })
      );
    },
    [dispatch, invitation, isPlatformLinked, openPlatformLink, form]
  );

  if (!invitation) {
    return null;
  }

  const declineButton = (
    <LoaderButton
      fullWidth
      variant="outlined"
      loading={updating}
      className={classNames(classes.button, classes.decline)}
      onClick={() => onClickHandler(TournamentPlayerStatus.Declined)}
    >
      Decline
    </LoaderButton>
  );

  const acceptButton = (
    <WithPaidActionButton
      label="Accept"
      tooltipText="Join Tournament"
      amount={entryFee}
      isLoading={updating}
      onClick={() => onClickHandler(TournamentPlayerStatus.Standby)}
    >
      {({ isCanNotEnter, text, onClick }) => (
        <LoaderButton
          color="primary"
          variant="contained"
          loading={updating}
          className={classes.button}
          onClick={onClick}
          disabled={isCanNotEnter}
        >
          {text}
        </LoaderButton>
      )}
    </WithPaidActionButton>
  );

  return (
    <TournamentAlert
      direction="column"
      title={`${invitation?.captainName} has invited you to join ${invitation?.teamName} in this tournament!`}
      description={
        checkinTime ? `Tournament check-in starts in ${checkinTime}` : 'Tournament check-in started'
      }
      controls={
        <FormProvider {...form}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>{acceptButton}</Box>

          <Box mt={2}>{declineButton}</Box>
        </FormProvider>
      }
    />
  );
};
